import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import type { Preaching } from '../../types';

const BG = '#1C1C1E';
const CARD = '#2C2C2E';
const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL;
const SUPABASE_ANON = import.meta.env.VITE_SUPABASE_ANON_KEY;

function getYouTubeId(url: string): string | null {
  const match = url?.match(/(?:v=|youtu\.be\/)([A-Za-z0-9_-]{11})/);
  return match ? match[1] : null;
}

export default function PreachingDetail() {
  const { id } = useParams<{ id: string }>();
  const [preaching, setPreaching] = useState<Preaching | null>(null);
  const [loading, setLoading] = useState(true);
  const [summarizing, setSummarizing] = useState(false);
  const [summary, setSummary] = useState<string>('');
  const [refs, setRefs] = useState<string[]>([]);

  useEffect(() => {
    supabase.from('preachings').select('*').eq('id', id!).single()
      .then(({ data }) => {
        setPreaching(data);
        setLoading(false);
        if (data) {
          if (data.summary) {
            setSummary(data.summary);
            setRefs(data.scripture_refs || []);
          } else {
            // On-demand: call edge function
            setSummarizing(true);
            fetch(`${SUPABASE_URL}/functions/v1/summarize-preaching`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${SUPABASE_ANON}`,
              },
              body: JSON.stringify({ preaching_id: data.id }),
            })
            .then(r => r.json())
            .then(res => {
              if (res.summary) { setSummary(res.summary); setRefs(res.scripture_refs || []); }
            })
            .finally(() => setSummarizing(false));
          }
        }
      });
  }, [id]);

  if (loading) return (
    <div className="min-h-screen animate-pulse" style={{ backgroundColor: BG }}>
      <div className="h-56" style={{ backgroundColor: CARD }} />
      <div className="p-4 space-y-3">
        <div className="h-6 rounded" style={{ backgroundColor: CARD, width: '80%' }} />
        <div className="h-4 rounded" style={{ backgroundColor: CARD, width: '50%' }} />
      </div>
    </div>
  );

  if (!preaching) return (
    <div className="text-center py-16" style={{ backgroundColor: BG }}>
      <p style={{ color: '#8E8E93' }}>Predicación no encontrada</p>
      <Link to="/preachings" className="mt-3 block" style={{ color: '#4A90D9' }}>← Volver</Link>
    </div>
  );

  const ytId = getYouTubeId(preaching.youtube_url || '');

  return (
    <div className="min-h-screen" style={{ backgroundColor: BG }}>
      {/* Video player */}
      {ytId ? (
        <div className="w-full" style={{ aspectRatio: '16/9', backgroundColor: '#000' }}>
          <iframe
            src={`https://www.youtube.com/embed/${ytId}?rel=0`}
            className="w-full h-full"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          />
        </div>
      ) : preaching.thumbnail_url ? (
        <img src={preaching.thumbnail_url} alt={preaching.title} className="w-full h-52 object-cover" />
      ) : (
        <div className="w-full h-40" style={{ background: 'linear-gradient(180deg, #0D2B8E 0%, #1C1C1E 100%)' }} />
      )}

      {/* Back button */}
      <div className="px-4 pt-3">
        <Link to="/preachings" className="text-sm" style={{ color: '#4A90D9' }}>← Predicaciones</Link>
      </div>

      {/* Info */}
      <div className="p-4 space-y-3">
        <div className="rounded-lg p-4" style={{ backgroundColor: CARD }}>
          <h1 className="text-xl font-bold text-white leading-tight">{preaching.title}</h1>
          {(preaching as any).speaker && (
            <p className="text-sm mt-2" style={{ color: '#4A90D9' }}>👤 {(preaching as any).speaker}</p>
          )}
        </div>

        {/* Resumen on-demand */}
        <div className="rounded-lg p-4" style={{ backgroundColor: CARD }}>
          <h2 className="font-bold text-white mb-2">📝 Ideas principales</h2>
          {summarizing ? (
            <div className="space-y-2 animate-pulse">
              <div className="h-3 rounded" style={{ backgroundColor: '#3A3A3C' }} />
              <div className="h-3 rounded" style={{ backgroundColor: '#3A3A3C', width: '85%' }} />
              <div className="h-3 rounded" style={{ backgroundColor: '#3A3A3C', width: '70%' }} />
              <p className="text-xs mt-2" style={{ color: '#8E8E93' }}>✨ Generando resumen con IA...</p>
            </div>
          ) : summary ? (
            <p className="text-sm leading-relaxed" style={{ color: '#B3B3B3' }}>{summary}</p>
          ) : (
            <p className="text-sm" style={{ color: '#8E8E93' }}>No disponible</p>
          )}
        </div>

        {/* Versículos */}
        {refs.length > 0 && (
          <div className="rounded-lg p-4" style={{ backgroundColor: CARD }}>
            <h2 className="font-bold text-white mb-3">📖 Versículos mencionados</h2>
            <div className="flex flex-wrap gap-2">
              {refs.map((ref, i) => (
                <span key={i} className="text-sm px-3 py-1 rounded-md font-medium"
                  style={{ backgroundColor: '#0D2B8E', color: '#4A90D9' }}>
                  {ref}
                </span>
              ))}
            </div>
          </div>
        )}

        {ytId && (
          <a
            href={`https://www.youtube.com/watch?v=${ytId}`}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center justify-center gap-2 w-full py-3 rounded-lg font-semibold text-white"
            style={{ backgroundColor: '#FF0000' }}
          >
            <span>▶</span> Ver en YouTube
          </a>
        )}
      </div>
    </div>
  );
}
